import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Badge, Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { Handshake, Info, User, UsersThree, Wallet } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import {
  ICustoPessoalItem,
  useFinanceiroComparativoFranquias,
  useFinanceiroCustoPessoal,
  useFinanceiroCustoPessoalDetalhe,
  useFinanceiroFolha,
} from '../../../../hooks/useFinanceiro';
import { AnimatedBar } from '../components/AnimatedBar';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { Segmented } from '../components/Segmented';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';
import { CustoArvore } from './CustoArvore';
import { PessoasTab } from './PessoasTab';
import { PorFilial } from './PorFilial';

const ORDEM_GRUPOS = ['Folha/Encargos', 'Benefícios', 'Estagiários', 'Sócios'];
const INK = '#0F172A';
const MUTED = '#94A3B8';
const ACCENT = '#DB2777';
const fmtPct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;
const pct = (parte: number, todo: number) => (todo > 0 ? (parte / todo) * 100 : 0);

const ABAS = [
  { value: 'custo', label: 'Custo (Conta Azul)' },
  { value: 'filial', label: 'Por filial' },
  { value: 'quadro', label: 'Quadro (Domínio)' },
];
const LENTES = [
  { value: 'cat', label: 'Por categoria' },
  { value: 'pessoa', label: 'Por pessoa' },
];
const STATUS_OPCOES = [
  { value: 'ativ', label: 'Ativos' },
  { value: 'demit', label: 'Demitidos' },
  { value: 'todos', label: 'Todos' },
];
const STATUS_LABEL: Record<string, string> = {
  ativ: 'ativos',
  demit: 'demitidos',
  todos: 'colaboradores',
};

const Barra = ({ valor, max, cor }: { valor: number; max: number; cor: string }) => (
  <Box mt={1}>
    <AnimatedBar pct={valor > 0 ? Math.max(3, (valor / Math.max(1, max)) * 100) : 0} cor={cor} />
  </Box>
);

/** Tile de KPI do header — sub aceita ReactNode; eyebrow/accentTop p/ o bloco "Quadro". */
const CruzKpi = ({
  label,
  valor,
  sub,
  cor,
  icon,
  info,
  eyebrow,
  accentTop,
}: {
  label: string;
  valor: string;
  sub: ReactNode;
  cor: string;
  icon: ReactNode;
  info?: ReactNode;
  eyebrow?: string;
  accentTop?: string;
}) => (
  <GlassCard
    intensity={accentTop ? 'strong' : 'medium'}
    p={4}
    borderTop={accentTop ? `2px solid ${accentTop}` : undefined}
  >
    <Flex align="center" gap={2} mb={2}>
      <Flex
        align="center"
        justify="center"
        w="30px"
        h="30px"
        borderRadius="9px"
        bg={`${cor}14`}
        color={cor}
        flexShrink={0}
      >
        {icon}
      </Flex>
      <Box minW={0}>
        {eyebrow && (
          <Text
            fontSize="9px"
            fontWeight={800}
            color={cor}
            textTransform="uppercase"
            letterSpacing="0.06em"
          >
            {eyebrow}
          </Text>
        )}
        <Text
          fontSize="11px"
          fontWeight={700}
          color={MUTED}
          textTransform="uppercase"
          letterSpacing="0.04em"
          noOfLines={1}
        >
          {label}
        </Text>
      </Box>
      {info && <Box ml="auto">{info}</Box>}
    </Flex>
    <Text fontSize="22px" fontWeight={800} color={INK} lineHeight={1.1}>
      {valor}
    </Text>
    <Box fontSize="11px" color={MUTED} mt={1}>
      {sub}
    </Box>
  </GlassCard>
);

export const FinanceiroCustoPessoalPage = () => {
  const f = useFinanceiroFilters();
  const params = { base: 'competencia' as const, ...f.apiParams };

  const { data, isError } = useFinanceiroCustoPessoal(params);
  const { data: detalhe } = useFinanceiroCustoPessoalDetalhe(params);
  const { data: folha } = useFinanceiroFolha();
  const { data: comparativo } = useFinanceiroComparativoFranquias({
    base: 'competencia',
    ...f.apiParams,
  });

  const itens = useMemo(() => data?.itens ?? [], [data]);
  const receitaTotal = data?.receita_total ?? 0;

  const [aba, setAba] = useState('custo');
  const [lente, setLente] = useState('cat');
  const [status, setStatus] = useState('ativ');

  // Estado de expansão da árvore (elevado p/ sobreviver à troca de lente/aba).
  // Grupos: default ABERTOS → guardamos os FECHADOS (vazio = todos abertos).
  const [gruposFechados, setGruposFechados] = useState<Set<string>>(new Set());
  const [categoriasAbertas, setCategoriasAbertas] = useState<Set<string>>(new Set());
  const [pessoasAbertas, setPessoasAbertas] = useState<Set<string>>(new Set());
  const [foco, setFoco] = useState<{ categoria: string; valor: number } | null>(null);

  const apiKey = JSON.stringify(f.apiParams);
  useEffect(() => {
    // Mudou período/franquia: dado recarrega; limpa expansões profundas órfãs.
    setCategoriasAbertas(new Set());
    setPessoasAbertas(new Set());
    setFoco(null);
  }, [apiKey]);

  // Quadro do Domínio: respeita a franquia global (conta_origem = nome da filial, 1:1)
  // além do status GLOBAL. Franquia vazia = consolidado (todas as filiais).
  const franquiaKey = f.contaOrigem.join(',');
  const colaboradoresFolha = useMemo(() => {
    const base = folha?.colaboradores ?? [];
    if (!franquiaKey) return base;
    const sel = new Set(franquiaKey.split(','));
    return base.filter(c => c.franquia != null && sel.has(c.franquia));
  }, [folha, franquiaKey]);
  const quadro = useMemo(
    () =>
      colaboradoresFolha.filter(c => {
        if (status === 'todos') return true;
        const ativo = (c.status || '').toLowerCase().startsWith('ativ');
        return status === 'ativ' ? ativo : !ativo;
      }),
    [colaboradoresFolha, status],
  );

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const custoTotal = useMemo(() => itens.reduce((s, i) => s + i.valor, 0), [itens]);
  const custoSocios = useMemo(
    () => itens.filter(i => i.grupo === 'Sócios').reduce((s, i) => s + i.valor, 0),
    [itens],
  );
  const custoColab = custoTotal - custoSocios;
  const headcount = quadro.length;
  const folhaDominio = quadro.reduce((s, c) => s + (c.salario || 0), 0);
  const ativosCount = useMemo(
    () => colaboradoresFolha.filter(c => (c.status || '').toLowerCase().startsWith('ativ')).length,
    [colaboradoresFolha],
  );
  const custoMedio = ativosCount ? custoColab / ativosCount : 0;
  const pctReceita = pct(custoTotal, receitaTotal);

  // ── Árvore: grupos ──────────────────────────────────────────────────────────
  const grupos = useMemo(() => {
    const map = new Map<string, ICustoPessoalItem[]>();
    itens.forEach(it => {
      const arr = map.get(it.grupo) ?? [];
      arr.push(it);
      map.set(it.grupo, arr);
    });
    const nomes = [
      ...ORDEM_GRUPOS.filter(g => map.has(g)),
      ...Array.from(map.keys()).filter(g => !ORDEM_GRUPOS.includes(g)),
    ];
    return nomes.map(nome => ({ nome, itens: map.get(nome) as ICustoPessoalItem[] }));
  }, [itens]);

  const gruposAbertos = useMemo(
    () => new Set(grupos.filter(g => !gruposFechados.has(g.nome)).map(g => g.nome)),
    [grupos, gruposFechados],
  );
  const abertos = { grupos: gruposAbertos, categorias: categoriasAbertas, pessoas: pessoasAbertas };

  const flip = (set: Set<string>, key: string) => {
    const n = new Set(set);
    if (n.has(key)) n.delete(key);
    else n.add(key);
    return n;
  };
  const toggleGrupo = (nome: string) => setGruposFechados(prev => flip(prev, nome));
  const togglePessoa = (key: string) => setPessoasAbertas(prev => flip(prev, key));
  const toggleCategoria = (cat: string) => {
    const abrindo = !categoriasAbertas.has(cat);
    setCategoriasAbertas(prev => flip(prev, cat));
    const it = itens.find(i => i.categoria === cat);
    setFoco(abrindo && it ? { categoria: cat, valor: it.valor } : null);
  };

  // ── Detalhe (lente "Por pessoa") ─────────────────────────────────────────
  const colaboradores = detalhe?.colaboradores ?? [];
  const totalDetalhe = detalhe?.total ?? 0;
  const maxColab = Math.max(1, ...colaboradores.map(c => c.valor));

  // Folha por filial (Domínio) recomputada a partir do quadro filtrado.
  const filiaisQuadro = useMemo(() => {
    const map = new Map<string, { qtd: number; salario_total: number }>();
    quadro.forEach(c => {
      const fr = c.franquia || '—';
      const cur = map.get(fr) ?? { qtd: 0, salario_total: 0 };
      cur.qtd += 1;
      cur.salario_total += c.salario || 0;
      map.set(fr, cur);
    });
    return Array.from(map.entries())
      .map(([franquia, v]) => ({ franquia, ...v }))
      .sort((a, b) => b.salario_total - a.salario_total);
  }, [quadro]);

  const filtroRegeAba = aba === 'quadro' || aba === 'filial';

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Despesas & Pessoal"
          titulo="Custo de pessoal"
          subtitulo="Desembolso real (Conta Azul) e o quadro do escritório (Domínio) — dois mundos que não casam 1:1"
          icon={<UsersThree size={24} weight="fill" />}
          cor={ACCENT}
          actions={
            <Flex align="center" gap={2}>
              <Text
                fontSize="11px"
                fontWeight={700}
                color={MUTED}
                textTransform="uppercase"
                letterSpacing="0.05em"
                display={{ base: 'none', sm: 'block' }}
              >
                Quadro
              </Text>
              <Box
                opacity={filtroRegeAba ? 1 : 0.4}
                pointerEvents={filtroRegeAba ? 'auto' : 'none'}
                transition="opacity .15s"
              >
                <Segmented
                  value={status}
                  onChange={setStatus}
                  accent={ACCENT}
                  options={STATUS_OPCOES}
                />
              </Box>
              <InfoPopover title="Situação do quadro">
                Filtra apenas o <b>Quadro do Domínio</b> (folha, headcount, abas Por filial e
                Quadro). O custo do Conta Azul <b>não</b> se separa por situação — o salário é pago
                ao banco em lote. Por isso o filtro fica desabilitado na aba Custo.
              </InfoPopover>
            </Flex>
          }
          info={
            <Text>
              Custo de pessoal real (Conta Azul) por categoria, com drill até o lançamento, mais o
              cruzamento com o quadro do escritório no Domínio (filiais por <code>external_id</code>
              ).
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar o custo de pessoal.</Text>
          </GlassCard>
        )}

        {/* KPIs — bloco Custo (3) + bloco Quadro (1, destacado) */}
        <SimpleGrid columns={{ base: 2, xl: 4 }} spacing={4} mb={6}>
          <CruzKpi
            label="Custo total (CA)"
            valor={brl(custoTotal)}
            cor="#DC2626"
            icon={<UsersThree size={17} weight="duotone" />}
            info={
              <InfoPopover title="Custo total de pessoal">
                Soma de todas as despesas de pessoal lançadas no Conta Azul no período/franquia,
                incluindo sócios. É o desembolso real — não a folha registrada no Domínio.
              </InfoPopover>
            }
            sub={
              <>
                <Text as="span" fontWeight={700} color={pctReceita > 60 ? '#DC2626' : '#0F172A'}>
                  {fmtPct(pctReceita)} da receita
                </Text>{' '}
                · folha + encargos + benef. + sócios
                {foco && (
                  <Text fontSize="11px" color={ACCENT} fontWeight={600} mt={0.5} noOfLines={1}>
                    + {foco.categoria}: {fmtPct(pct(foco.valor, receitaTotal))} da receita
                  </Text>
                )}
              </>
            }
          />
          <CruzKpi
            label="Custo / colab. ativo"
            valor={brl(custoMedio)}
            cor="#0EA5E9"
            icon={<User size={17} weight="duotone" />}
            sub={`${ativosCount} ativos · CA sem sócios`}
            info={
              <InfoPopover title="Custo médio por colaborador">
                Custo de colaboradores (Conta Azul, sem sócios) ÷ nº de ativos do Domínio. Mede o
                desembolso médio real por cabeça, com encargos e benefícios.
              </InfoPopover>
            }
          />
          <CruzKpi
            label="Sócios"
            valor={brl(custoSocios)}
            cor="#7C3AED"
            icon={<Handshake size={17} weight="duotone" />}
            sub="retiradas / pró-labore"
          />
          <CruzKpi
            eyebrow="Quadro do escritório"
            label="Folha Domínio"
            valor={brl(folhaDominio)}
            cor={ACCENT}
            accentTop={ACCENT}
            icon={<Wallet size={17} weight="duotone" />}
            sub={`${headcount} ${STATUS_LABEL[status]} · base salarial`}
            info={
              <InfoPopover title="Folha registrada (Domínio)">
                Soma dos salários-base do quadro no Domínio, conforme a situação selecionada. A
                diferença para o custo do Conta Azul são encargos, benefícios e equipe em outra
                base.
              </InfoPopover>
            }
          />
        </SimpleGrid>

        {/* Seletor de aba */}
        <Box mb={5} overflowX="auto">
          <Segmented value={aba} onChange={setAba} accent={ACCENT} options={ABAS} />
        </Box>

        {/* ── Aba Custo (Conta Azul) ── */}
        {aba === 'custo' && (
          <>
            <Flex align="center" justify="space-between" gap={3} wrap="wrap" mb={3}>
              <Flex
                align="center"
                gap={2}
                bg="rgba(15,23,42,0.03)"
                borderRadius="lg"
                px={3}
                py={1.5}
                maxW={{ md: '70%' }}
              >
                <Box as={Info} size={14} color={MUTED} flexShrink={0} />
                <Text fontSize="12px" color="#64748B">
                  Desembolso real do Conta Azul. <b>Não</b> se separa por situação do colaborador
                  (salário é pago ao banco em lote) — o filtro Ativos/Demitidos age só no Quadro.
                </Text>
              </Flex>
              <Box overflowX="auto">
                <Segmented value={lente} onChange={setLente} accent={ACCENT} options={LENTES} />
              </Box>
            </Flex>

            {lente === 'cat' && (
              <GlassCard intensity="strong" p={{ base: 3, md: 5 }}>
                <CustoArvore
                  grupos={grupos}
                  custoTotal={custoTotal}
                  receitaTotal={receitaTotal}
                  params={params}
                  abertos={abertos}
                  toggleGrupo={toggleGrupo}
                  toggleCategoria={toggleCategoria}
                  togglePessoa={togglePessoa}
                />
              </GlassCard>
            )}

            {lente === 'pessoa' && (
              <GlassCard intensity="strong" p={{ base: 3, md: 5 }}>
                {colaboradores.length === 0 ? (
                  <Text fontSize="sm" color={MUTED} textAlign="center" py={6}>
                    Sem colaboradores para o período.
                  </Text>
                ) : (
                  <Flex direction="column" gap={1}>
                    <Flex
                      justify="space-between"
                      fontSize="10px"
                      fontWeight={700}
                      color={MUTED}
                      textTransform="uppercase"
                      px={2}
                      pb={2}
                    >
                      <Text>Colaborador</Text>
                      <Text>Custo · %</Text>
                    </Flex>
                    {colaboradores.map(c => (
                      <Box
                        key={`${c.pessoa_nome}-${c.valor}`}
                        px={2}
                        py={2}
                        borderRadius="md"
                        _hover={{ bg: 'rgba(15,23,42,0.03)' }}
                      >
                        <Flex justify="space-between" align="center" gap={3}>
                          <Box minW={0}>
                            <Text fontSize="sm" fontWeight={600} color={INK} noOfLines={1}>
                              {c.pessoa_nome || '—'}
                            </Text>
                            <Flex align="center" gap={2} mt={0.5}>
                              {c.departamento ? (
                                <Text fontSize="11px" color="#71717A" noOfLines={1}>
                                  {c.departamento}
                                  {c.cargo ? ` · ${c.cargo}` : ''}
                                </Text>
                              ) : (
                                <Badge colorScheme="orange" fontSize="9px">
                                  Sem vínculo no Domínio
                                </Badge>
                              )}
                            </Flex>
                          </Box>
                          <Flex align="center" gap={3} whiteSpace="nowrap">
                            <Text fontSize="11px" color={MUTED} w="44px" textAlign="right">
                              {fmtPct(pct(c.valor, totalDetalhe))}
                            </Text>
                            <Text
                              fontSize="sm"
                              fontWeight={700}
                              color={INK}
                              w="110px"
                              textAlign="right"
                            >
                              {brl(c.valor)}
                            </Text>
                          </Flex>
                        </Flex>
                        <Barra valor={c.valor} max={maxColab} cor="#DC2626" />
                      </Box>
                    ))}
                  </Flex>
                )}
              </GlassCard>
            )}
          </>
        )}

        {/* ── Por filial ── */}
        {aba === 'filial' && (
          <PorFilial key={status} filiais={filiaisQuadro} comparativo={comparativo?.itens ?? []} />
        )}

        {/* ── Quadro (Domínio) ── */}
        {aba === 'quadro' && <PessoasTab colaboradores={quadro} statusKey={status} />}
      </Box>
    </GlassPageBg>
  );
};
